import {
  CABINET_PERMISSION,
  HIDDEN_CABINET_PERMISSIONS,
  businessKeys,
  hasCabinetPermission,
  type CabinetPermissionKey,
  type PermissionSource,
} from './auth-permissions';
import { currentAuthRole, isBusinessUserRole } from './auth-roles';

/** Раздел бизнес-кабинета: сегмент роута внутри `businesses/:business_id` и ключи, которые его открывают. */
export interface CabinetSection {
  path: string;
  label: string;
  /**
   * Ключ права; несколько — раздел открывает любой из них; null — раздел доступен
   * любому участнику бизнеса, ключа под него в каталоге нет.
   */
  permission: CabinetPermissionKey | readonly CabinetPermissionKey[] | null;
}

/**
 * BE-807: карта «раздел → ключ» — один источник для меню, гвардов и редиректа. Держать её
 * в трёх местах значило бы однажды показать пункт меню, ведущий на роут, который сам себя
 * закрывает. Порядок совпадает с порядком пунктов в меню кабинета: по нему же выбирается
 * раздел, куда увести сотрудника с закрытой страницы.
 */
export const CABINET_SECTIONS: readonly CabinetSection[] = [
  { path: '', label: 'Главная', permission: null },
  { path: 'profile', label: 'Профиль', permission: CABINET_PERMISSION.profileView },
  // BE-1346: партнёрские ссылки связанного игрового профиля. Право то же, что у профиля —
  // ровно то, под которым бэк отдаёт GET /businesses/{id}/referral. Стоит ПОСЛЕ профиля
  // намеренно: `firstAvailableCabinetPath` уводит сотрудника в первый раздел с правом, и
  // перед профилем этот пункт перехватил бы редирект у всех, у кого есть profile.view.
  { path: 'partner-links', label: 'Партнёрские ссылки', permission: CABINET_PERMISSION.profileView },
  { path: 'marketplace', label: 'Магазины', permission: CABINET_PERMISSION.shopsView },
  { path: 'promotions', label: 'Акции', permission: CABINET_PERMISSION.promotionsView },
  { path: 'scan', label: 'Сканер', permission: CABINET_PERMISSION.scannerScan },
  // Экран построен на GET /businesses/{id}/orders, поэтому таблица требует orders.view.
  // Но штатный пресет «Сканер» выдаёт кассиру scanner.history без заказов, и закрыть
  // раздел одними заказами значило бы отобрать у него историю, которую ему выдали:
  // пускаем по любому из двух ключей, а таблицу внутри экран гейтит сам.
  {
    path: 'scan-history',
    label: 'История сканов',
    permission: [CABINET_PERMISSION.ordersView, CABINET_PERMISSION.scannerHistory],
  },
  { path: 'tickets', label: 'Мои заявки', permission: null },
  { path: 'accounting', label: 'Счета', permission: CABINET_PERMISSION.invoicesView },
  { path: 'users', label: 'Сотрудники', permission: CABINET_PERMISSION.staffView },
  { path: 'reports', label: 'Отчёты', permission: CABINET_PERMISSION.reportsView },
  // API-токены для внешнего магазина. Выпуск и отзыв внутри гейтятся integrations.manage.
  { path: 'integrations', label: 'Интеграции', permission: CABINET_PERMISSION.integrationsView },
  // Раздел держит и заявки бизнеса (под applications.view), и публичные документы
  // платформы — оферту, политику, согласие на обработку ПДн. Закрыть его целиком значило
  // бы спрятать от сотрудника юридические документы, открытые вообще всем.
  { path: 'documents', label: 'Документы', permission: null },
  { path: 'notifications', label: 'Уведомления', permission: null },
];

/**
 * Блоки главной. Каждый идёт под своим правом, и если не доступен ни один, показывать
 * там нечего: остаются заголовок и четыре нуля.
 */
const CABINET_DASHBOARD_KEYS: readonly CabinetPermissionKey[] = [
  CABINET_PERMISSION.scannerScan,
  CABINET_PERMISSION.scannerHistory,
  CABINET_PERMISSION.promotionsView,
  CABINET_PERMISSION.ordersView,
  CABINET_PERMISSION.invoicesView,
];

/** Раздел, куда уводить сотрудника, которому закрыто всё остальное. Ключа не требует. */
const CABINET_FALLBACK_PATH = 'tickets';

/** Ключи, закрывающие раздел; пустой массив — раздел открыт всем участникам бизнеса. */
export function cabinetSectionPermissions(path: string): readonly CabinetPermissionKey[] {
  const permission = CABINET_SECTIONS.find((section) => section.path === path)?.permission ?? null;
  if (permission === null) {
    return [];
  }
  return Array.isArray(permission) ? permission : [permission as CabinetPermissionKey];
}

/** Доступен ли раздел в этом бизнесе. */
export function isCabinetSectionAvailable(
  state: PermissionSource,
  path: string,
  businessId: string | null | undefined,
): boolean {
  const keys = cabinetSectionPermissions(path);
  return keys.length === 0 || keys.some((key) => hasCabinetPermission(state, key, businessId));
}

/**
 * Первый доступный раздел в порядке меню. Главная кандидатом не считается: она сама
 * может оказаться пустой, и редирект с неё закольцевался бы сам на себя.
 *
 * Разделы с ключом идут раньше открытых всем: если сотруднику выдали ровно одно право,
 * увести его надо туда, где это право работает, а не в «Мои заявки», которые просто
 * стоят выше в меню.
 */
export function firstAvailableCabinetPath(
  state: PermissionSource,
  businessId: string | null | undefined,
): string {
  const candidates = CABINET_SECTIONS.filter(
    (section) => section.path !== '' && isCabinetSectionAvailable(state, section.path, businessId),
  );
  const granted = candidates.find((section) => cabinetSectionPermissions(section.path).length > 0);
  return (granted ?? candidates[0])?.path ?? CABINET_FALLBACK_PATH;
}

/**
 * Есть ли на главной хоть один блок, который этот пользователь вправе увидеть.
 *
 * BE-1483: у сотрудника «только сканер» — нет: из блоков ему доступна одна кнопка
 * «Сканировать», и его «Главная» — сам сканер. Отсюда же меню прячет пункт «Главная».
 */
export function hasCabinetDashboardContent(
  state: PermissionSource,
  businessId: string | null | undefined,
): boolean {
  return (
    !isCabinetScannerOnly(state, businessId)
    && CABINET_DASHBOARD_KEYS.some((key) => hasCabinetPermission(state, key, businessId))
  );
}

/** Ключи кассира: пресет «Сканер» (`cabinet_scanner`) выдаёт ровно их. */
const CABINET_SCANNER_ONLY_KEYS: ReadonlySet<string> = new Set<string>([
  CABINET_PERMISSION.scannerScan,
  CABINET_PERMISSION.scannerHistory,
]);

/**
 * BE-1483: выдан ли сотруднику в этом бизнесе только сканер.
 *
 * - Только сотрудник: владельцу бэкенд отдаёт весь каталог кабинета, админский контур и
 *   бухгалтер в кабинет не ходят.
 * - Права не загружены — не решаем: набор ещё пуст. На `hasCabinetPermission`, который в этом
 *   состоянии отвечает «да» на любой ключ, условие не построить.
 * - Скрытые легаси-ключи витрины (`HIDDEN_CABINET_PERMISSIONS`) не считаются: они ничего не
 *   открывают, а владелец их не видит — остаются у кассира, которому раньше выдавали
 *   «Ведение магазина», а потом оставили один сканер.
 * - Одна история сканов без сканера — не сюда: уводить её некуда, поведение прежнее.
 */
export function isCabinetScannerOnly(
  state: PermissionSource,
  businessId: string | null | undefined,
): boolean {
  if (!isBusinessUserRole(currentAuthRole(state))) {
    return false;
  }
  const keys = businessKeys(state, businessId).filter((key) => key.startsWith('cabinet.'));
  return (
    keys.includes(CABINET_PERMISSION.scannerScan)
    && keys.every((key) => CABINET_SCANNER_ONLY_KEYS.has(key) || HIDDEN_CABINET_PERMISSIONS.has(key))
  );
}
